'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Accordion,
  Button,
  Grid,
  Group,
  Input,
  InputLabel,
  Modal,
  Space,
  Textarea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { DropzoneCloudinary } from '@/src/components/EditorText/Dropzone';
import { useAuthValidation } from '@/src/lib/hooks/useAuthValidation';
import { Product } from '@/src/types/Header';
import { FullSizeLoader } from '../Loader/FullSizeLoader';

export const EditionProductModale = ({ product }: { product: Product }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const router = useRouter();

  // **Form States**
  const [titleFr, setTitleFr] = useState(product.title.fr ?? product.title);
  const [titleEn, setTitleEn] = useState(product.title.en ?? product.title);
  const [mainImage, setMainImage] = useState<string | null>(product.mainImage);
  const [descriptionFr, setDescriptionFr] = useState(product.description.fr ?? product.description);
  const [descriptionEn, setDescriptionEn] = useState(product.description.en ?? product.description);
  const [price, setPrice] = useState(product.price || 0);
  const [length, setLength] = useState(product.dimentions.length?.toString() || '');
  const [width, setWidth] = useState(product.dimentions.width?.toString() || '');
  const [height, setHeight] = useState(product.dimentions.height?.toString() || '');
  const [depth, setDepth] = useState(product.dimentions.depth?.toString() || '');
  const [category] = useState(product.category || '');
  const [tags] = useState(product.tags || []);
  const [seoTitle] = useState(product.seoTitle || '');
  const [seoDescription] = useState(product.seoDescription || '');
  const [seoKeywords] = useState(product.seoKeywords || []);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { isLoading } = useAuthValidation();
  if (isLoading) {
    return <FullSizeLoader />;
  }

  // 🛑 Validate Fields
  const validateFields = () => {
    const newErrors: Record<string, string> = {};

    if (!mainImage) {
      newErrors.mainImage = 'Une image est requise.';
    }
    if (!titleFr) {
      newErrors.titleFr = 'Le titre en français est requis.';
    }
    if (!descriptionFr) {
      newErrors.descriptionFr = 'La description en français est requise.';
    }
    if (price && isNaN(Number(price))) {
      newErrors.price = 'Le prix doit être un nombre.';
    }
    if (length && isNaN(Number(length))) {
      newErrors.length = 'Longueur invalide.';
    }
    if (width && isNaN(Number(width))) {
      newErrors.width = 'Largeur invalide.';
    }
    if (height && isNaN(Number(height))) {
      newErrors.height = 'Hauteur invalide.';
    }
    if (depth && isNaN(Number(depth))) {
      newErrors.depth = 'Profondeur invalide.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🚀 Save Product Changes
  const saveProduct = async () => {
    if (!validateFields()) {
      return;
    }

    const updatedProduct: Product = {
      ...product,
      title: { en: titleEn, fr: titleFr },
      mainImage: mainImage || '',
      description: { en: descriptionEn, fr: descriptionFr },
      price: price ? Number(price) : 0,
      dimentions: {
        length: length ? parseFloat(length) : undefined,
        width: width ? parseFloat(width) : undefined,
        height: height ? parseFloat(height) : undefined,
        depth: depth ? parseFloat(depth) : undefined,
      },
      category,
      tags,
      seoTitle,
      seoDescription,
      seoKeywords,
    };

    const response = await fetch(`/api/products/${product._id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedProduct),
    });

    if (!response.ok) {
      console.error('Failed to save product.');
    } else {
      console.log('Product saved successfully.');
      close();
      router.push('/products');
      router.refresh();
    }
  };

  // ❌ Delete Product
  const deleteProduct = async () => {
    const response = await fetch(`/api/products/${product._id}`, { method: 'DELETE' });

    if (!response.ok) {
      console.error('Failed to delete product.');
    } else {
      console.log('Product deleted successfully.');
      router.push('/products');
    }
  };

  return (
    <>
      <Group>
        <Button onClick={open} color="green">
          Modifier le Produit
        </Button>
        <Button onClick={deleteProduct} color="red">
          Supprimer le Produit
        </Button>
      </Group>

      <Modal opened={opened} onClose={close} title="Modifier le Produit">
        <InputLabel>Image Principale *</InputLabel>
        <DropzoneCloudinary
          setImageUrl={setMainImage}
          imageUrl={mainImage}
          error={errors.mainImage}
        />
        <Space h="md" />

        <InputLabel>Titre Français *</InputLabel>
        <Input
          value={titleFr}
          onChange={(e) => setTitleFr(e.target.value)}
          onBlur={validateFields}
          placeholder="Titre du produit"
          error={errors.titleFr}
        />
        <Space h="md" />

        <InputLabel>Description Française *</InputLabel>
        <Textarea
          value={descriptionFr}
          onChange={(e) => setDescriptionFr(e.target.value)}
          onBlur={validateFields}
          placeholder="Description"
          error={errors.descriptionFr}
        />
        <Space h="md" />

        {/* Optional Fields in Accordion */}
        <Accordion variant="contained">
          <Accordion.Item value="anglais">
            <Accordion.Control>
              Ajouter un titre et une description en Anglais (Optionnel)
            </Accordion.Control>
            <Accordion.Panel>
              <InputLabel>Titre Anglais</InputLabel>
              <Input
                value={titleEn}
                onChange={(e) => setTitleEn(e.target.value)}
                placeholder="Titre en anglais"
              />

              <Space h="md" />
              <InputLabel>Description Anglaise</InputLabel>
              <Textarea
                value={descriptionEn}
                onChange={(e) => setDescriptionEn(e.target.value)}
                placeholder="Description en anglais"
              />
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="prix-dimensions">
            <Accordion.Control>Ajouter un prix et des dimensions (Optionnel)</Accordion.Control>
            <Accordion.Panel>
              <InputLabel>Prix (€)</InputLabel>
              <Input
                type="number"
                value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
                placeholder="Prix"
                error={errors.price}
              />

              <Grid gutter="md">
                <Grid.Col span={6}>
                  <InputLabel>Longueur (cm)</InputLabel>
                  <Input
                    type="number"
                    value={length}
                    onChange={(e) => setLength(e.target.value)}
                    placeholder="Ex: 50"
                    error={errors.length}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <InputLabel>Largeur (cm)</InputLabel>
                  <Input
                    type="number"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    placeholder="Ex: 30"
                    error={errors.width}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <InputLabel>Hauteur (cm)</InputLabel>
                  <Input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="Ex: 20"
                    error={errors.height}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <InputLabel>Profondeur (cm)</InputLabel>
                  <Input
                    type="number"
                    value={depth}
                    onChange={(e) => setDepth(e.target.value)}
                    placeholder="Ex: 10"
                    error={errors.depth}
                  />
                </Grid.Col>
              </Grid>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        <Space h="lg" />
        <Button onClick={saveProduct} color="blue" fullWidth>
          Enregistrer les modifications
        </Button>
      </Modal>
    </>
  );
};
